/**
 * Pricing Intelligence Service
 * AI-powered pricing optimization for solopreneurs
 * Analyzes market data, competitor rates, and customer behavior
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Service categories for pricing analysis
 */
export const SERVICE_CATEGORIES = [
  'Web Development',
  'Mobile Development',
  'UI/UX Design',
  'Graphic Design',
  'Content Writing',
  'Digital Marketing',
  'SEO Services',
  'Consulting',
  'Project Management',
  'Data Analysis',
  'Video Production',
  'Photography',
  'Virtual Assistant',
  'Social Media Management',
  'Custom',
];

/**
 * Pricing strategies
 */
export const PRICING_STRATEGIES = {
  COST_PLUS: 'cost_plus', // Cost + markup percentage
  VALUE_BASED: 'value_based', // Based on value delivered to client
  COMPETITIVE: 'competitive', // Based on competitor pricing
  HOURLY: 'hourly', // Time-based hourly rate
  PROJECT: 'project', // Fixed project pricing
  SUBSCRIPTION: 'subscription', // Recurring subscription
};

/**
 * Analyze internal pricing data (your own invoices and costs)
 */
export const analyzeInternalPricing = async (userId) => {
  if (!db) {
    return {
      totalRevenue: 0,
      totalExpenses: 0,
      avgInvoiceAmount: 0,
      profitMargin: 0,
      serviceAnalysis: {},
      invoiceCount: 0,
    };
  }

  try {
    // Fetch user's invoices
    const invoicesRef = collection(db, 'invoices');
    const invoicesQuery = query(
      invoicesRef,
      where('userId', '==', userId),
      where('status', '==', 'paid'),
      orderBy('issueDate', 'desc')
    );
    const invoicesSnapshot = await getDocs(invoicesQuery);
    const invoices = invoicesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Fetch user's expenses
    const expensesRef = collection(db, 'expenses');
    const expensesQuery = query(
      expensesRef,
      where('userId', '==', userId),
      orderBy('date', 'desc')
    );
    const expensesSnapshot = await getDocs(expensesQuery);
    const expenses = expensesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Calculate metrics
    const totalRevenue = invoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
    const totalExpenses = expenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
    const avgInvoiceAmount = invoices.length > 0 ? totalRevenue / invoices.length : 0;
    const profitMargin = totalRevenue > 0 ? ((totalRevenue - totalExpenses) / totalRevenue) * 100 : 0;

    // Analyze by service type (from line items)
    const serviceAnalysis = {};
    invoices.forEach(invoice => {
      invoice.lineItems?.forEach(item => {
        const service = item.description || 'Uncategorized';
        if (!serviceAnalysis[service]) {
          serviceAnalysis[service] = {
            count: 0,
            totalRevenue: 0,
            avgRate: 0,
            minRate: Infinity,
            maxRate: 0,
          };
        }
        serviceAnalysis[service].count += 1;
        serviceAnalysis[service].totalRevenue += item.amount || 0;
        const rate = item.rate || 0;
        serviceAnalysis[service].minRate = Math.min(serviceAnalysis[service].minRate, rate);
        serviceAnalysis[service].maxRate = Math.max(serviceAnalysis[service].maxRate, rate);
      });
    });

    // Calculate average rates
    Object.keys(serviceAnalysis).forEach(service => {
      const analysis = serviceAnalysis[service];
      analysis.avgRate = analysis.count > 0 ? analysis.totalRevenue / analysis.count : 0;
      if (analysis.minRate === Infinity) analysis.minRate = 0;
    });

    // Client analysis
    const clientMetrics = {};
    invoices.forEach(invoice => {
      const clientId = invoice.clientId;
      if (!clientMetrics[clientId]) {
        clientMetrics[clientId] = {
          clientName: invoice.clientName,
          invoiceCount: 0,
          totalRevenue: 0,
          avgInvoiceValue: 0,
          paymentSpeed: [], // days to payment
        };
      }
      clientMetrics[clientId].invoiceCount += 1;
      clientMetrics[clientId].totalRevenue += invoice.totalAmount || 0;

      // Calculate payment speed
      if (invoice.paidAt && invoice.issueDate) {
        const issueDate = invoice.issueDate.toDate ? invoice.issueDate.toDate() : new Date(invoice.issueDate);
        const paidDate = invoice.paidAt.toDate ? invoice.paidAt.toDate() : new Date(invoice.paidAt);
        const daysToPayment = Math.floor((paidDate - issueDate) / (1000 * 60 * 60 * 24));
        clientMetrics[clientId].paymentSpeed.push(daysToPayment);
      }
    });

    // Calculate client averages
    Object.keys(clientMetrics).forEach(clientId => {
      const metrics = clientMetrics[clientId];
      metrics.avgInvoiceValue = metrics.invoiceCount > 0 ? metrics.totalRevenue / metrics.invoiceCount : 0;
      const speeds = metrics.paymentSpeed;
      metrics.avgPaymentDays = speeds.length > 0 ? speeds.reduce((a, b) => a + b, 0) / speeds.length : 0;
    });

    return {
      summary: {
        totalRevenue,
        totalExpenses,
        netProfit: totalRevenue - totalExpenses,
        profitMargin,
        avgInvoiceAmount,
        invoiceCount: invoices.length,
        expenseCount: expenses.length,
      },
      serviceAnalysis,
      clientMetrics,
      rawData: {
        invoices: invoices.slice(0, 10), // Last 10 invoices
        expenses: expenses.slice(0, 20), // Last 20 expenses
      },
    };
  } catch (error) {
    console.error('Error analyzing internal pricing:', error);
    throw error;
  }
};

/**
 * Calculate optimal pricing based on costs and target profit margin
 */
export const calculateOptimalPrice = (costs, targetMarginPercent = 30, strategy = 'cost_plus') => {
  const {
    directCosts = 0,
    indirectCosts = 0,
    timeHours = 0,
    desiredHourlyRate = 0,
  } = costs;

  const totalCosts = directCosts + indirectCosts + (timeHours * desiredHourlyRate);

  switch (strategy) {
    case PRICING_STRATEGIES.COST_PLUS:
      return totalCosts * (1 + targetMarginPercent / 100);

    case PRICING_STRATEGIES.HOURLY:
      const hourlyOverhead = indirectCosts / Math.max(timeHours, 1);
      return (desiredHourlyRate + hourlyOverhead) * (1 + targetMarginPercent / 100);

    default:
      return totalCosts * (1 + targetMarginPercent / 100);
  }
};

/**
 * Save a service pricing profile
 */
export const saveServicePricing = async (userId, pricingData) => {
  try {
    const pricing = {
      userId,
      serviceName: pricingData.serviceName,
      category: pricingData.category,
      description: pricingData.description || '',
      currentRate: pricingData.currentRate,
      suggestedRate: pricingData.suggestedRate,
      strategy: pricingData.strategy || PRICING_STRATEGIES.HOURLY,
      costs: {
        directCosts: pricingData.directCosts || 0,
        indirectCosts: pricingData.indirectCosts || 0,
        timeHours: pricingData.timeHours || 0,
      },
      targetMargin: pricingData.targetMargin || 30,
      competitorRates: pricingData.competitorRates || [],
      marketData: pricingData.marketData || {},
      lastAnalyzedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, 'servicePricing'), pricing);
    return { pricingId: docRef.id, ...pricing };
  } catch (error) {
    console.error('Error saving service pricing:', error);
    throw error;
  }
};

/**
 * Get all service pricing for user
 */
export const getServicePricing = async (userId) => {
  if (!db) return [];

  try {
    const q = query(
      collection(db, 'servicePricing'),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting service pricing:', error);
    return [];
  }
};

/**
 * Update service pricing
 */
export const updateServicePricing = async (pricingId, updateData) => {
  try {
    const pricingRef = doc(db, 'servicePricing', pricingId);
    await updateDoc(pricingRef, {
      ...updateData,
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating service pricing:', error);
    throw error;
  }
};

/**
 * AI-powered pricing analysis using external AI service
 * This would integrate with services like OpenAI, Claude, or Abacus AI
 */
export const getAIPricingRecommendations = async (analysisData) => {
  try {
    // This is a placeholder for AI integration
    // In production, you would call your AI service here
    // For now, we'll return rule-based recommendations

    const {
      internalData,
      competitorData,
      serviceCategory,
      country,
      targetMargin,
    } = analysisData;

    const recommendations = {
      suggestedRate: 0,
      confidence: 'medium',
      reasoning: [],
      adjustmentFactors: [],
      marketPosition: 'competitive',
    };

    // Calculate base rate from internal data
    const avgCurrentRate = internalData.summary.avgInvoiceAmount;
    const profitMargin = internalData.summary.profitMargin;

    recommendations.reasoning.push(`Your current average invoice is ${avgCurrentRate.toFixed(2)}`);
    recommendations.reasoning.push(`Your current profit margin is ${profitMargin.toFixed(1)}%`);

    // Adjust for target margin
    if (profitMargin < targetMargin) {
      const increaseNeeded = ((targetMargin - profitMargin) / 100) * avgCurrentRate;
      recommendations.suggestedRate = avgCurrentRate + increaseNeeded;
      recommendations.reasoning.push(`To reach ${targetMargin}% margin, increase rates by ${increaseNeeded.toFixed(2)}`);
      recommendations.adjustmentFactors.push({
        factor: 'Profit Margin Gap',
        impact: 'increase',
        amount: increaseNeeded,
      });
    } else {
      recommendations.suggestedRate = avgCurrentRate;
      recommendations.reasoning.push('Your current pricing achieves target margin');
    }

    // Competitor comparison
    if (competitorData && competitorData.length > 0) {
      const avgCompetitorRate = competitorData.reduce((sum, c) => sum + c.rate, 0) / competitorData.length;
      const competitorDiff = avgCompetitorRate - recommendations.suggestedRate;

      if (Math.abs(competitorDiff) > avgCompetitorRate * 0.2) {
        if (competitorDiff > 0) {
          recommendations.reasoning.push(`Market rates are ${competitorDiff.toFixed(2)} higher - consider premium positioning`);
          recommendations.marketPosition = 'budget';
        } else {
          recommendations.reasoning.push(`Your rates are above market average - ensure value justification`);
          recommendations.marketPosition = 'premium';
        }
      } else {
        recommendations.marketPosition = 'competitive';
      }
    }

    // Country/market adjustments (simplified)
    const countryMultipliers = {
      'United States': 1.0,
      'United Kingdom': 0.85,
      'Canada': 0.8,
      'Australia': 0.85,
      'Philippines': 0.3,
      'India': 0.25,
      'Singapore': 0.9,
    };

    const multiplier = countryMultipliers[country] || 0.5;
    recommendations.countryAdjustment = multiplier;
    recommendations.localizedRate = recommendations.suggestedRate * multiplier;

    return recommendations;
  } catch (error) {
    console.error('Error getting AI recommendations:', error);
    throw error;
  }
};

/**
 * Save competitor pricing data
 */
export const saveCompetitorData = async (userId, competitorData) => {
  try {
    const competitor = {
      userId,
      competitorName: competitorData.competitorName,
      serviceCategory: competitorData.serviceCategory,
      rate: competitorData.rate,
      rateType: competitorData.rateType || 'hourly', // hourly, project, monthly
      country: competitorData.country || '',
      source: competitorData.source || '', // website, proposal, marketplace
      sourceUrl: competitorData.sourceUrl || '',
      notes: competitorData.notes || '',
      qualityRating: competitorData.qualityRating || 3, // 1-5
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, 'competitorPricing'), competitor);
    return { competitorId: docRef.id, ...competitor };
  } catch (error) {
    console.error('Error saving competitor data:', error);
    throw error;
  }
};

/**
 * Get competitor pricing data
 */
export const getCompetitorData = async (userId, filters = {}) => {
  if (!db) return [];

  try {
    let q = query(
      collection(db, 'competitorPricing'),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    );

    if (filters.serviceCategory) {
      q = query(q, where('serviceCategory', '==', filters.serviceCategory));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting competitor data:', error);
    return [];
  }
};

/**
 * Generate pricing report
 */
export const generatePricingReport = async (userId, options = {}) => {
  try {
    const internalData = await analyzeInternalPricing(userId);
    const competitorData = await getCompetitorData(userId, options.filters);
    const servicePricing = await getServicePricing(userId);

    const report = {
      generatedAt: new Date(),
      userId,
      summary: internalData.summary,
      services: servicePricing,
      competitors: competitorData,
      recommendations: [],
    };

    // Generate recommendations for each service
    for (const service of servicePricing) {
      const recommendations = await getAIPricingRecommendations({
        internalData,
        competitorData: competitorData.filter(c => c.serviceCategory === service.category),
        serviceCategory: service.category,
        country: options.country || 'United States',
        targetMargin: service.targetMargin || 30,
      });

      report.recommendations.push({
        serviceName: service.serviceName,
        ...recommendations,
      });
    }

    // Save report to database
    const docRef = await addDoc(collection(db, 'pricingReports'), {
      ...report,
      createdAt: serverTimestamp(),
    });

    return { reportId: docRef.id, ...report };
  } catch (error) {
    console.error('Error generating pricing report:', error);
    throw error;
  }
};

/**
 * Subscribe to pricing updates
 */
export const subscribeToPricingUpdates = (userId, callback) => {
  const q = query(
    collection(db, 'servicePricing'),
    where('userId', '==', userId),
    orderBy('updatedAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const pricing = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(pricing);
  });
};
